import {
  CreateDateColumn,
  UpdateDateColumn,
  Entity,
  Index,
  Column,
  RelationId,
  ManyToOne,
  OneToMany,
  PrimaryGeneratedColumn
} from 'typeorm'
import { ObjectType, Field, ID, Int } from 'type-graphql'
import { Domain } from '@things-factory/shell'
import { LabelingWorkflow } from './labeling-workflow.js'
import { WorkflowExecutionStep } from './workflow-execution-step.js'

/**
 * WorkflowExecution Entity
 *
 * Represents a single execution instance of a workflow.
 * Tracks execution status, timing, and results.
 */
@ObjectType({
  description: 'Entity representing a workflow execution instance'
})
@Entity('workflow_executions')
@Index('ix_workflow_execution_0', ['domain', 'workflow', 'status'], {
  where: '"deleted_at" IS NULL'
})
@Index('ix_workflow_execution_1', ['domain', 'startedAt'], {
  where: '"deleted_at" IS NULL'
})
export class WorkflowExecution {
  @PrimaryGeneratedColumn('uuid')
  @Field(type => ID, { description: 'Unique execution identifier' })
  id: string

  @ManyToOne(type => Domain)
  @Field(type => Domain, { nullable: true, description: 'Domain to which this execution belongs' })
  domain?: Domain

  @RelationId((execution: WorkflowExecution) => execution.domain)
  domainId?: string

  @ManyToOne(type => LabelingWorkflow, workflow => workflow.executions)
  @Field(type => LabelingWorkflow, { description: 'The workflow being executed' })
  workflow: LabelingWorkflow

  @RelationId((execution: WorkflowExecution) => execution.workflow)
  @Field({ description: 'Workflow ID' })
  workflowId: string

  @Column()
  @Field({ description: 'Workflow name (cached)' })
  workflowName: string

  @Column({ type: 'varchar', length: 50 })
  @Field({ description: 'Execution status: running, completed, failed' })
  status: string

  @OneToMany(type => WorkflowExecutionStep, step => step.execution, { cascade: true })
  @Field(type => [WorkflowExecutionStep], { description: 'Execution steps' })
  steps: WorkflowExecutionStep[]

  @Column({ type: 'text', nullable: true })
  @Field({ nullable: true, description: 'Execution summary' })
  summary?: string

  @Column({ type: 'text', nullable: true })
  @Field({ nullable: true, description: 'Error message if failed' })
  error?: string

  @Column({ type: 'timestamptz' })
  @Field({ description: 'Execution start time' })
  startedAt: Date

  @Column({ type: 'timestamptz', nullable: true })
  @Field({ nullable: true, description: 'Execution completion time' })
  completedAt?: Date

  @Column({ type: 'int', nullable: true })
  @Field(type => Int, { nullable: true, description: 'Total duration in milliseconds' })
  totalDurationMs?: number

  @CreateDateColumn({ type: 'timestamptz' })
  @Field({ description: 'Creation timestamp' })
  createdAt: Date

  @UpdateDateColumn({ type: 'timestamptz' })
  @Field({ description: 'Last update timestamp' })
  updatedAt: Date

  @Column({ type: 'timestamptz', nullable: true })
  @Field({ nullable: true, description: 'Soft delete timestamp' })
  deletedAt?: Date
}
